import type { lotusHealthSchemas } from '@lotushealth/lotushealth_js_sdk';
import { z } from 'zod';
import type { ZodType } from 'zod';

export type CopilotToolDefinition = lotusHealthSchemas.HostageToolDefinitionT;

export type CopilotToolCall = {
  toolCallId: string;
  toolName: string;
  input: unknown;
};

export type CopilotToolContext = {
  /** The clinician message that initiated the current model/tool loop. */
  originatingUserMessageId: string | undefined;
};

export type CopilotToolHandlerResult<TOutput = unknown> =
  | { output: TOutput }
  | { errorText: string }
  | { deferred: true };

export type CopilotToolSpec<TName extends string = string, TInput = unknown, TOutput = unknown> = {
  /** Must match the tool definition name used by the model. */
  name: TName;
  /** Runtime validator for the tool input payload. */
  inputSchema: ZodType<TInput>;
  /**
   * Return a tool definition to inject, or null if this tool is currently unavailable.
   * Use this for lifecycle gating (e.g. only show RxWizard tools when mounted).
   */
  getDefinition: () => CopilotToolDefinition | null;
  /** Execute the tool call with validated input. */
  handle(
    input: TInput,
    context: CopilotToolContext
  ): Promise<CopilotToolHandlerResult<TOutput>> | CopilotToolHandlerResult<TOutput>;
};

export type AddToolOutputFn = (
  args:
    | {
        state?: 'output-available';
        toolCallId: string;
        tool: string;
        output: unknown;
        errorText?: never;
      }
    | {
        state: 'output-error';
        toolCallId: string;
        tool: string;
        output?: never;
        errorText: string;
      }
) => void | Promise<void>;

export function defineToolSpec<TName extends string, TInput, TOutput>(
  spec: CopilotToolSpec<TName, TInput, TOutput>
): CopilotToolSpec<TName, TInput, TOutput> {
  return spec;
}

export function toToolInputJsonSchema<TInput>(
  inputSchema: ZodType<TInput>
): CopilotToolDefinition['input_schema'] {
  return z.toJSONSchema(inputSchema) as CopilotToolDefinition['input_schema'];
}
